import { useCallback, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { AlertTriangle, Trash2, X } from "lucide-react";
import { Button } from "./Button";
import { AnimatePresence, motion } from "framer-motion";
import { FRAMER_TRANSITIONS } from "@/lib/motion";

interface ConfirmDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void | Promise<void>;
    title: string;
    description?: string;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: "default" | "danger";
    loading?: boolean;
}

export function ConfirmDialog({
    open,
    onClose,
    onConfirm,
    title,
    description,
    message,
    confirmLabel = "Bestätigen",
    cancelLabel = "Abbrechen",
    variant = "default",
    loading,
}: ConfirmDialogProps) {
    const confirmRef = useRef<HTMLButtonElement>(null);
    const cancelRef = useRef<HTMLButtonElement>(null);

    // Focus trap + Escape
    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        },
        [onClose],
    );

    useEffect(() => {
        if (open) {
            document.addEventListener("keydown", handleKeyDown);
            // Focus cancel button for danger variant (prevent accidental destructive action)
            const focusTarget = variant === "danger" ? cancelRef : confirmRef;
            setTimeout(() => focusTarget.current?.focus(), 100);
            // Prevent body scroll
            document.body.style.overflow = "hidden";
            return () => {
                document.removeEventListener("keydown", handleKeyDown);
                document.body.style.overflow = "";
            };
        }
    }, [open, handleKeyDown, variant]);

    return (
        <AnimatePresence>
            {open && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        className="fixed inset-0 z-40 bg-neutral-900/20 backdrop-blur-sm"
                        aria-hidden="true"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={FRAMER_TRANSITIONS.component}
                    />

                    {/* Dialog */}
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div
                            className="w-full max-w-md rounded-2xl bg-white dark:bg-neutral-800 shadow-drawer"
                            role="alertdialog"
                            aria-modal="true"
                            aria-label={title}
                            initial={{ opacity: 0, scale: 0.95, y: 8 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 8 }}
                            transition={FRAMER_TRANSITIONS.emphasis}
                        >
                            {/* Header */}
                            <div className="flex items-start gap-4 px-6 pt-6">
                                <div
                                    className={cn(
                                        "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl",
                                        variant === "danger" ? "bg-red-50" : "bg-brand-50",
                                    )}
                                >
                                    {variant === "danger" ? (
                                        <Trash2 className="h-5 w-5 text-red-500" />
                                    ) : (
                                        <AlertTriangle className="h-5 w-5 text-brand-500" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-base font-bold tracking-tight text-neutral-900 dark:text-neutral-100">
                                        {title}
                                    </h3>
                                    <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">
                                        {message || description || ""}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="h-9 w-9 flex items-center justify-center rounded-xl text-neutral-400 dark:text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-700 hover:text-neutral-600 dark:hover:text-neutral-300 transition-all duration-200"
                                    aria-label="Schließen"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>

                            {/* Footer */}
                            <div className="flex justify-end gap-3 px-6 py-5 mt-2 border-t border-neutral-100 dark:border-neutral-700">
                                <Button
                                    ref={cancelRef}
                                    variant="outline"
                                    size="md"
                                    onClick={onClose}
                                    disabled={loading}
                                >
                                    {cancelLabel}
                                </Button>
                                <Button
                                    ref={confirmRef}
                                    variant={variant === "danger" ? "danger" : "primary"}
                                    size="md"
                                    onClick={onConfirm}
                                    loading={loading}
                                >
                                    {confirmLabel}
                                </Button>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}
